import React from "react";
import { Helmet } from "react-helmet-async";

export interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  author?: string;
  twitterCard?: string;
  siteName?: string;
  canonicalUrl?: string;
  // New SEO features
  noindex?: boolean;
  nofollow?: boolean;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleAuthor?: string;
  locale?: string;
  alternateLocales?: string[];
  geo?: {
    region?: string;
    placename?: string;
    position?: string;
  };
  preloadResources?: Array<{
    href: string;
    as: string;
    type?: string;
    crossorigin?: string;
  }>;
  prefetchDNS?: string[];
  preconnect?: string[];
}

const SEO: React.FC<SEOProps> = ({
  title = "Version2 - Powerful add-ons to the services you love",
  description = "Version2 provides a collection of add-ons to services and tools you already use: extensions, themes, scripts, and other tools that rocket the services you already know and love to the next level.",
  keywords = "add-ons, extensions, productivity, airtable, plutio, automation, tools, version2",
  image = "https://version2.io/favicon.png",
  url = "https://version2.io",
  type = "website",
  author = "Version2",
  twitterCard = "summary_large_image",
  siteName = "Version2",
  canonicalUrl,
  noindex = false,
  nofollow = false,
  articlePublishedTime,
  articleModifiedTime,
  articleAuthor,
  locale = "en_US",
  alternateLocales = [],
  geo,
  preloadResources = [],
  prefetchDNS = [],
  preconnect = [],
}) => {
  const finalTitle = title.includes("Version2") ? title : `${title} | Version2`;
  const finalUrl = canonicalUrl || url;
  const robotsContent = `${noindex ? "noindex" : "index"}, ${nofollow ? "nofollow" : "follow"}`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="title" content={finalTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <link rel="canonical" href={finalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={finalUrl} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={siteName} />

      {/* Twitter */}
      <meta property="twitter:card" content={twitterCard} />
      <meta property="twitter:url" content={finalUrl} />
      <meta property="twitter:title" content={finalTitle} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={image} />

      {/* Additional SEO */}
      <meta name="robots" content={robotsContent} />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />

      {/* Article Meta Tags */}
      {articlePublishedTime && (
        <meta property="article:published_time" content={articlePublishedTime} />
      )}
      {articleModifiedTime && (
        <meta property="article:modified_time" content={articleModifiedTime} />
      )}
      {articleAuthor && <meta property="article:author" content={articleAuthor} />}

      {/* Locale Meta Tags */}
      <meta property="og:locale" content={locale} />
      {alternateLocales.map((altLocale, index) => (
        <meta key={index} property="og:locale:alternate" content={altLocale} />
      ))}

      {/* Geographic Meta Tags */}
      {geo?.region && <meta name="geo.region" content={geo.region} />}
      {geo?.placename && <meta name="geo.placename" content={geo.placename} />}
      {geo?.position && <meta name="geo.position" content={geo.position} />}
      {geo?.position && <meta name="ICBM" content={geo.position} />}

      {/* Performance: DNS Prefetch */}
      {prefetchDNS.map((domain, index) => (
        <link key={`dns-${index}`} rel="dns-prefetch" href={domain} />
      ))}

      {/* Performance: Preconnect */}
      {preconnect.map((domain, index) => (
        <link key={`preconnect-${index}`} rel="preconnect" href={domain} crossOrigin="anonymous" />
      ))}

      {/* Performance: Preload Resources */}
      {preloadResources.map((resource, index) => {
        const crossOrigin =
          resource.crossorigin === "anonymous" ||
          resource.crossorigin === "use-credentials"
            ? resource.crossorigin
            : undefined;

        return (
          <link
            key={`preload-${index}`}
            rel="preload"
            href={resource.href}
            as={resource.as}
            type={resource.type}
            crossOrigin={crossOrigin}
          />
        );
      })}

      {/* Mobile App Meta Tags */}
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="Version2" />
      <meta name="format-detection" content="telephone=no" />

      {/* Security Headers */}
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      <meta name="referrer" content="no-referrer-when-downgrade" />
    </Helmet>
  );
};

export default SEO;
